import { useContext, useRef, useState } from "react";
import "./HarvestLogPage.css";
import { HarvestLogModalBody } from "../modals/HarvestLogModalBody";
import { HarvestLogContext } from "../store/HarvestLogContext";
import Searchbar from "../components/Searchbar";
import type { HarvestLog } from "../utils/types";
import { useReactToPrint } from "react-to-print";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import "../components/Print.css";
import { ModalContext } from "../store/ModalContext";
import LoadingSpinner from "../../LoadingSpinner";

const HarvestLogPage = () => {
  const { harvestLogs, deleteLog, deleteAllLogs, loading, error } =
    useContext(HarvestLogContext);
  const { openModal, closeModal } = useContext(ModalContext);
  const [currentTypedSearch, setCurrentTypedSearch] = useState("");
  const [searchLogList, setSearchLogList] =
    useState<HarvestLog[]>();
  const printableRef = useRef<HTMLDivElement | null>(null);
  //TODO add in the backend and on each log the name of the person adding the log. To prevent dubble logs.
  const logList = harvestLogs.map((log) => {
    const endDateExists =
      log.endDate !== log.startDate && ` - ${formatDate(log.endDate)}`;
    return (
      <li className="log-item info-card" key={log.logId}>
        <p className="bold">
          {formatDate(log.startDate)}
          {endDateExists}
        </p>

        <ul>
          {log.products.map((product) => {
            return (
              <li key={product.product} className="first-word-capitalized ">
                {product.product}, {product.quantity} kg
              </li>
            );
          })}
          {log.comment !== "" && (
            <p className="log-no-margin-bottom">
              <span className="bold">Comment:</span> {log.comment}
            </p>
          )}
        </ul>

        <button
          className="btn log-info-card-btn"
          onClick={() => handleDelete(log.logId)}
        >
          <FontAwesomeIcon icon={faTrash} style={{ color: "#ffffff" }} />
        </button>
      </li>
    );
  });
  const searchedLogList = searchLogList?.map((log) => {
    const endDateExists =
      log.endDate !== log.startDate && ` - ${formatDate(log.endDate)}`;
    return (
      <li className="log-item info-card" key={log.logId}>
        <p className="bold">
          {formatDate(log.startDate)}
          {endDateExists}
        </p>

        <ul>
          {log.products.map((product) => {
            return (
              <li key={product.product} className="first-word-capitalized ">
                {product.product}, {product.quantity} kg
              </li>
            );
          })}
          {log.comment !== "" && (
            <p className="log-no-margin-bottom">
              <span className="bold">Comment:</span> {log.comment}
            </p>
          )}
        </ul>

        <button
          className="btn log-info-card-btn"
          onClick={() => handleDelete(log.logId)}
        >
          <FontAwesomeIcon icon={faTrash} style={{ color: "#ffffff" }} />
        </button>
      </li>
    );
  });

  const displayedLogList =
    currentTypedSearch !== "" ? searchedLogList : logList;
  function filterProducts(value: string) {
    const filteredLogs = harvestLogs.filter((item) =>
      item.products.some((product) =>
        product.product.toLowerCase().includes(value.toLowerCase()),
      ),
    );
    setSearchLogList(filteredLogs);
    setCurrentTypedSearch(value);
  }

  const handlePrint = useReactToPrint({
    contentRef: printableRef,
    documentTitle: "List of Harvest Logs",
  });
  function handleDelete(id: string) {
    openModal({
      title: "WARNING",
      body: "Are you sure you want to delete this log?",
      buttons: [
        { text: "No", callback: closeModal },
        {
          text: "Yes",
          loadingText: "Deleting...",
          callback: async () => {
            await deleteLog(id);
            closeModal();
          },
        },
      ],
    });
  }

  function handleDeleteAll() {
    openModal({
      title: "WARNING",
      body: "Are you sure you want to delete all harvest logs? This cannot be undone.",
      buttons: [
        { text: "No", callback: closeModal },
        {
          text: "Yes, delete all",
          loadingText: "Deleting...",
          callback: async () => {
            await deleteAllLogs();
            setCurrentTypedSearch("");
            setSearchLogList(undefined);
            closeModal();
          },
        },
      ],
    });
  }
  function formatDate(dateString: string) {
    const date = new Date(dateString);

    const month = date.toLocaleString("en-US", { month: "long" });
    const day = date.getDate();

    const getOrdinal = (n: number) => {
      if (n > 3 && n < 21) return "th";
      switch (n % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    };

    return `${month} ${day}${getOrdinal(day)}`;
  }

  return (
    <div className="page-container">
      <h1>Harvest Logs</h1>
      <div className="btn-aligned-right-box">
        <button className="btn btn-aligned-right" onClick={handlePrint}>
          <span>🖨️</span> Print or Save
        </button>
      </div>
      <div className="page-content">
        {error && (
          <p className="error-message" role="alert">
            {error}
          </p>
        )}

        <div className="header-plus-button">
          <div className="harvest-logs-buttons-container">
            <button
              className="btn"
              onClick={() =>
                openModal({
                  title: "Create Log",
                  showCloseButton: true,
                  body: <HarvestLogModalBody onClose={closeModal} />,
                })
              }
            >
              Add Log
            </button>
            {harvestLogs.length > 0 && (
              <button
                className="btn"
                type="button"
                onClick={handleDeleteAll}
                disabled={loading}
              >
                Delete All Logs
              </button>
            )}
          </div>
          <h2>Previous Logs</h2>
        </div>
        <Searchbar
          action={filterProducts}
          placeholder={"Search for product..."}
        />
        {!loading && harvestLogs.length === 0 && (
          <p>There are no current harvest logs.</p>
        )}
        {loading && harvestLogs.length === 0 ? (
          <LoadingSpinner />
        ) : (
          <div ref={printableRef} className="print">
            <ul>{displayedLogList}</ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default HarvestLogPage;
